import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import { NoData, Spinner } from '@rio-cloud/rio-uikit';
import { fetchVehicleTechnicalSupportCalls } from '../../../../../api/tas';
import type { IOccurrence } from '../../../types';
import { typesOcorrenceStatusIds } from '../../../constants';
import { convertMinutesToDaysAndHours } from '../../../../../utils/dateUtils';

const columns = [
    'intl-msg:support.vehicleHistory.columns.callNumber',
    'intl-msg:support.vehicleHistory.columns.plateChassis',
    'intl-msg:support.vehicleHistory.columns.dn',
    'intl-msg:support.vehicleHistory.columns.status',
    'intl-msg:support.vehicleHistory.columns.owner',
    'intl-msg:support.vehicleHistory.columns.createdAt',
    'intl-msg:support.vehicleHistory.columns.waitingMinutes',
];

interface Props {
    chassis?: string;
    customer?: string;
}

export default function TechnicalSupportCalls({ chassis, customer }: Props) {
    const intl = useIntl();
    const { pathname } = useLocation();
    // Prefixo da persona na rota atual (ex.: /control-tower, /dealership, /fleet-manager)
    const personaBasePath = `/${pathname.split('/').filter(Boolean)[0] ?? ''}`;
    const [rows, setRows] = useState<IOccurrence[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!chassis) {
            setRows([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        fetchVehicleTechnicalSupportCalls(chassis)
            .then(data => {
                if (!cancelled) {
                    setRows(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRows([]);
                    setHasError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [chassis]);

    if (isLoading) {
        return (
            <div className='display-flex justify-content-center padding-20'>
                <Spinner isDoubleSized />
            </div>
        );
    }

    if (hasError || !rows.length) {
        return (
            <NoData
                text={intl.formatMessage({
                    id: hasError
                        ? 'intl-msg:support.vehicleHistory.loadError'
                        : 'intl-msg:support.vehicleHistory.noData',
                })}
                className='text-size-16 padding-20'
            />
        );
    }

    return (
        <div className='table-responsive'>
            <table className='table table-head-filled'>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th key={column}>{intl.formatMessage({ id: column })}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const statusId = typesOcorrenceStatusIds[row.status as keyof typeof typesOcorrenceStatusIds];

                        return (
                            <tr key={row.id}>
                                <td>
                                    <Link to={`${personaBasePath}/support/details?id=${row.id}`}>
                                        {row.tasOccurrenceNumber || '-'}
                                    </Link>
                                </td>
                                <td>{row.chassis || row.licensePlate}</td>
                                <td>{row.dnDescription || row.dn}</td>
                                <td>{statusId ? intl.formatMessage({ id: statusId }) : row.status}</td>
                                <td>{customer || row.customer}</td>
                                <td>
                                    {row.createdAt ? moment.utc(row.createdAt).local().format('DD/MM/YYYY HH:mm') : '-'}
                                </td>
                                <td>
                                    {row.timeInStatus != null ? convertMinutesToDaysAndHours(row.timeInStatus) : '-'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
